/** tm/components/AIStepConfigModal.tsx — AI 스텝 설정 모달 (직접 설정 + 프리셋 저장) */
import { useState } from 'react';
import type { AIPreset } from '../types';
import { C, T, R } from './ui';

interface Props {
  aiType: 'ai_questionnaire' | 'ai_bizplan';
  presets: AIPreset[];
  initialPresetId?: string;
  initialPrompt?: string;
  initialFiles?: { label: string; name: string; s3_key: string }[];
  initialIncludeCollect?: boolean;
  onSave: (config: { presetId: string; prompt: string; files?: { label: string; name: string; s3_key: string }[]; includeCollect?: boolean }) => void;
  onClose: () => void;
  onNavigateToPreset?: () => void;
}

export default function AIStepConfigModal({
  aiType, presets, initialPresetId, initialPrompt, initialFiles, initialIncludeCollect, onSave, onClose, onNavigateToPreset,
}: Props) {
  const [presetId, setPresetId] = useState(initialPresetId || '');
  const [extraPrompt, setExtraPrompt] = useState(initialPrompt || '');
  const [directMode, setDirectMode] = useState(!initialPresetId);
  const [includeCollect, setIncludeCollect] = useState(initialIncludeCollect || false);
  const [files, setFiles] = useState<{ label: string; name: string; s3_key: string; file?: File }[]>(initialFiles || []);
  const [newFileLabel, setNewFileLabel] = useState('');
  const BIZPLAN_LABELS = ['사업계획서 양식', '작성 가이드', '선정 기준표', '샘플 사업계획서', '기타'];
  const [docLabels, setDocLabels] = useState<string[]>(() => {
    if (initialFiles && initialFiles.length > 0) return initialFiles.map(f => f.label);
    if (aiType === 'ai_bizplan') return BIZPLAN_LABELS;
    return [];
  });
  const [addingLabel, setAddingLabel] = useState(false);
  const [saveAsPreset, setSaveAsPreset] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [saving, setSaving] = useState(false);

  const filtered = presets.filter(p => p.type === aiType);
  const sel = filtered.find(p => p.id === presetId);
  const raw = sel as unknown as Record<string, unknown> | undefined;
  const presetFiles: { label?: string; name: string; s3_key: string }[] = raw && Array.isArray(raw.files) ? raw.files as { label?: string; name: string; s3_key: string }[] : [];
  const presetPrompt = sel?.prompt || '';
  const presetIncludeCollect = raw ? !!(raw.include_collect_files) : false;
  const title = aiType === 'ai_questionnaire' ? 'AI 질문지 설정' : 'AI 사업계획서 설정';

  function handlePresetSelect(id: string) {
    setPresetId(id);
    if (id) {
      setDirectMode(false);
      const p = filtered.find(x => x.id === id);
      if (p) {
        const r = p as unknown as Record<string, unknown>;
        setExtraPrompt((r.prompt as string) || '');
        setIncludeCollect(!!(r.include_collect_files));
        const newFiles = Array.isArray(r.files) ? (r.files as { label: string; name: string; s3_key: string }[]) : [];
        setFiles(newFiles);
        setDocLabels(newFiles.map(f => f.label || f.name));
      }
    }
  }

  async function handleSaveAsPreset() {
    if (!presetName.trim()) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token') || '';
      const fd = new FormData();
      fd.append('name', presetName.trim());
      fd.append('type', aiType);
      fd.append('prompt', extraPrompt);
      fd.append('include_collect_files', String(includeCollect));
      const labels: string[] = [];
      files.forEach((f, i) => {
        labels.push(f.label || f.name);
        if (f.file) fd.append('files', f.file);
      });
      fd.append('doc_labels', JSON.stringify(labels));
      await fetch('/api/tm/ai-presets', {
        method: 'POST', headers: { Authorization: 'Bearer ' + token }, body: fd,
      });
      setSaveAsPreset(false);
      setPresetName('');
    } catch { /* ignore */ }
    setSaving(false);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} />
      <div style={{ position: 'relative', width: 560, maxHeight: '90vh', background: '#fff', borderRadius: 12, display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '16px 20px', background: 'linear-gradient(135deg, #3730a3, #7C3AED)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: '12px 12px 0 0' }}>
          <div style={{ fontSize: T.lg, fontWeight: 700, color: '#fff' }}>🤖 {title}</div>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 8, border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.15)', cursor: 'pointer', fontSize: T.sm, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 20px' }}>

          {/* 모드 선택 */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
            <button onClick={() => setDirectMode(false)} style={{ flex: 1, padding: '8px 0', borderRadius: R.md, fontSize: T.xs, fontWeight: !directMode ? 700 : 400, cursor: 'pointer', border: `1px solid ${!directMode ? '#7C3AED' : C.border}`, background: !directMode ? '#F5F3FF' : '#fff', color: !directMode ? '#7C3AED' : C.sub }}>프리셋 선택</button>
            <button onClick={() => { setDirectMode(true); setPresetId(''); }} style={{ flex: 1, padding: '8px 0', borderRadius: R.md, fontSize: T.xs, fontWeight: directMode ? 700 : 400, cursor: 'pointer', border: `1px solid ${directMode ? '#7C3AED' : C.border}`, background: directMode ? '#F5F3FF' : '#fff', color: directMode ? '#7C3AED' : C.sub }}>직접 설정</button>
          </div>

          {/* 프리셋 선택 모드: 드롭다운 */}
          {!directMode && (
            <select value={presetId} onChange={e => handlePresetSelect(e.target.value)}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: T.md, outline: 'none', background: '#fff', boxSizing: 'border-box', marginBottom: 12 }}>
              <option value="">프리셋을 선택하세요</option>
              {filtered.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}

          {/* 상세 설정 (직접 설정 모드 또는 프리셋 선택 완료 시) */}
          {(directMode || presetId) && (
            <>
              {/* 자료수집 파일 자동 전달 */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 14 }}>🔄</span>
                  <span style={{ fontSize: T.sm }}>앞단계 자료수집 파일 자동 전달</span>
                </div>
                <div onClick={() => setIncludeCollect(!includeCollect)} style={{ width: 40, height: 22, borderRadius: 11, background: includeCollect ? '#059669' : '#D1D5DB', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
                  <div style={{ width: 18, height: 18, borderRadius: 9, background: '#fff', position: 'absolute', top: 2, left: includeCollect ? 20 : 2, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                </div>
              </div>

              {/* 항목별 파일 */}
              <div style={{ fontSize: T.xs, fontWeight: 600, color: '#7C3AED', marginBottom: 6 }}>📎 AI에게 전달할 파일</div>
              {docLabels.map((label, i) => {
                const uploaded = files.find(f => f.label === label);
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, height: 44, padding: '0 10px', borderRadius: 6, border: `1px solid ${uploaded ? '#7C3AED' : C.border}`, background: uploaded ? '#F5F3FF' : '#fff', marginBottom: 3 }}>
                    <span style={{ fontSize: 13 }}>{uploaded ? '✅' : '📌'}</span>
                    <span style={{ flex: 1, fontSize: T.xs, color: uploaded ? '#7C3AED' : C.text, fontWeight: uploaded ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {uploaded ? `${label} — ${uploaded.name}` : label}
                    </span>
                    {!uploaded && (
                      <label style={{ fontSize: T.xs, padding: '4px 10px', borderRadius: R.md, border: '1px solid #7C3AED', background: '#F5F3FF', color: '#7C3AED', cursor: 'pointer', fontWeight: 600, flexShrink: 0 }}>
                        📎 파일 선택
                        <input type="file" onChange={e => { const f = e.target.files?.[0]; if (f) setFiles(p => [...p, { label, name: f.name, s3_key: '', file: f }]); e.target.value = ''; }} style={{ display: 'none' }} />
                      </label>
                    )}
                    {uploaded && <button onClick={() => setFiles(p => p.filter(f => f.label !== label))} style={{ fontSize: T.xs, padding: '3px 6px', borderRadius: 4, border: '1px solid #DC262644', background: 'transparent', color: '#DC2626', cursor: 'pointer', flexShrink: 0 }}>✕</button>}
                    <button onClick={() => { setFiles(p => p.filter(f => f.label !== label)); setDocLabels(p => p.filter(l => l !== label)); }} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: T.xs, padding: '2px' }}>✕</button>
                  </div>
                );
              })}
              {addingLabel ? (
                <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                  <input value={newFileLabel} onChange={e => setNewFileLabel(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && newFileLabel.trim()) { setDocLabels(p => [...p, newFileLabel.trim()]); setNewFileLabel(''); } }} placeholder="항목명 입력" autoFocus style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid #7C3AED', fontSize: T.xs, outline: 'none' }} />
                  <button onClick={() => { if (newFileLabel.trim()) { setDocLabels(p => [...p, newFileLabel.trim()]); setNewFileLabel(''); } }} disabled={!newFileLabel.trim()} style={{ padding: '6px 12px', borderRadius: 6, border: 'none', background: newFileLabel.trim() ? '#7C3AED' : '#ddd', color: '#fff', fontSize: T.xs, fontWeight: 600, cursor: newFileLabel.trim() ? 'pointer' : 'default' }}>추가</button>
                  <button onClick={() => { setAddingLabel(false); setNewFileLabel(''); }} style={{ padding: '6px 8px', borderRadius: 6, border: `1px solid ${C.border}`, background: '#fff', color: C.sub, fontSize: T.xs, cursor: 'pointer' }}>취소</button>
                </div>
              ) : (
                <button onClick={() => setAddingLabel(true)} style={{ width: '100%', padding: '7px 0', marginTop: 4, marginBottom: 12, borderRadius: 6, border: '1.5px dashed #7C3AED44', background: 'transparent', color: '#7C3AED', fontSize: T.xs, fontWeight: 600, cursor: 'pointer' }}>+ 항목 추가</button>
              )}

              {/* 프롬프트 */}
              <div style={{ fontSize: T.xs, fontWeight: 600, color: '#7C3AED', marginBottom: 6 }}>🤖 AI 지시사항 (프롬프트)</div>
              <textarea value={extraPrompt} onChange={e => setExtraPrompt(e.target.value)} placeholder="AI에게 전달할 추가 지시사항을 입력하세요..." rows={5}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: T.sm, outline: 'none', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.6 }} />

              {/* 프리셋으로 저장 */}
              {!saveAsPreset ? (
                <button onClick={() => setSaveAsPreset(true)} style={{ marginTop: 10, padding: '6px 14px', borderRadius: 6, border: `1px solid #7C3AED44`, background: '#F5F3FF', color: '#7C3AED', fontSize: T.xs, fontWeight: 600, cursor: 'pointer' }}>💾 프리셋으로 저장</button>
              ) : (
                <div style={{ marginTop: 10, display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input value={presetName} onChange={e => setPresetName(e.target.value)} placeholder="프리셋 이름 입력" autoFocus style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: T.xs, outline: 'none' }} />
                  <button onClick={handleSaveAsPreset} disabled={saving || !presetName.trim()} style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: '#7C3AED', color: '#fff', fontSize: T.xs, fontWeight: 600, cursor: 'pointer', opacity: saving || !presetName.trim() ? 0.5 : 1 }}>{saving ? '저장 중...' : '저장'}</button>
                  <button onClick={() => setSaveAsPreset(false)} style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.border}`, background: '#fff', color: C.sub, fontSize: T.xs, cursor: 'pointer' }}>취소</button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: 8, background: '#F8FAFC' }}>
          <button onClick={onClose} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: `1px solid ${C.border}`, background: '#fff', color: C.sub, fontSize: T.md, fontWeight: 600, cursor: 'pointer' }}>취소</button>
          <button onClick={() => onSave({ presetId: directMode ? '' : presetId, prompt: extraPrompt, files, includeCollect })} style={{ flex: 2, padding: '10px 0', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg, #3730a3, #7C3AED)', color: '#fff', fontSize: T.md, fontWeight: 700, cursor: 'pointer' }}>💾 저장</button>
        </div>
      </div>
    </div>
  );
}
